import { EyeOff } from "lucide-react";

import { Hint } from "@/design-system/Hint.tsx";
import { toggleHidden, useHiddenListings } from "@/listings/listing.hidden.ts";
import { cn } from "@/design-system/cn.ts";

/**
 * Take this listing off the map, or put it back.
 *
 * The counterpart to TrackButton: that one makes a listing easier to find,
 * this one makes it stop existing. Hiding is local to this browser, and the
 * console's "Show hidden" switch is the way to reach a hidden listing again —
 * which the hint spells out, because a button that makes things vanish owes an
 * explanation of where they went.
 */
export function HideButton({
  id,
  onHidden,
  className,
}: {
  id: string;
  /** Fires on hide only, not restore — the card closes with the pin it sat on. */
  onHidden?: () => void;
  className?: string;
}) {
  const hidden = useHiddenListings();
  const on = hidden.has(id);

  return (
    <Hint
      label={on ? "Hidden from the map" : "Hide this listing"}
      detail={
        on
          ? "Click to restore it."
          : "Removes it from the map. Bring hidden listings back with Show hidden in the filters."
      }
      side="top"
    >
      <button
        type="button"
        onClick={() => {
          toggleHidden(id);
          if (!on) onHidden?.();
        }}
        aria-pressed={on}
        aria-label={on ? "Restore this listing" : "Hide this listing"}
        className={cn(
          "grid shrink-0 place-items-center rounded-lg border border-rule bg-ink-900/85 px-3 py-2.5 text-chalk-dim transition",
          "hover:border-rule-bright hover:text-chalk active:scale-95",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-ink",
          on && "border-alert/60 text-alert hover:border-alert hover:text-alert",
          className,
        )}
      >
        <EyeOff className="size-4" />
      </button>
    </Hint>
  );
}
